import { useEffect, useReducer, useRef, useState } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import { DIALOG_PAPER_SX, CANCEL_BUTTON_SX, PRIMARY_BUTTON_SX, colors as dialogColors } from '../Offers/TownPortalFlow/TownPortalOffersDialog.js';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from '../../../common/toast';
import logger from '../../../utils/logger';

const t = {
  ink: '#1F2328',
  muted: '#667085',
  faint: '#98A2B3',
  line: '#E4E7EC',
  lineStrong: '#D0D5DD',
  surface: '#FFFFFF',
  subtle: '#F7F8FA',
  blue: '#1E4FA3',
  blueHover: '#183F84',
  blueSoft: '#EEF2FB',
  danger: '#C62828',
  radius: 8,
  radiusSm: 6,
  pill: 999,
  font: 13,
  fontSm: 12,
};

const btnBase = {
  height: 32, px: 1.5, minWidth: 0, fontSize: t.font, textTransform: 'none',
  borderRadius: `${t.radiusSm}px`, boxShadow: 'none',
};

const s = {
  trigger: {
    height: 32, px: 1.25, gap: 0.75, minWidth: 0, maxWidth: 280,
    fontSize: t.font, fontWeight: 500, textTransform: 'none', color: t.ink,
    bgcolor: t.surface, border: `1px solid ${t.lineStrong}`, borderRadius: `${t.radiusSm}px`,
    '&:hover': { bgcolor: t.subtle, borderColor: t.faint },
    '& .MuiButton-startIcon': { m: 0, color: t.muted },
    '& .MuiButton-endIcon': { m: 0, color: t.muted },
  },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  paper: {
    mt: 0.75, width: 340, overflow: 'hidden',
    borderRadius: `${t.radius}px`, border: `1px solid ${t.line}`,
    boxShadow: '0 12px 32px rgba(16,24,40,.12), 0 2px 6px rgba(16,24,40,.06)',
  },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.75 },
  sectionTitle: { fontSize: t.font, fontWeight: 600, color: t.ink },
  meta: { fontSize: t.fontSm, color: t.muted },
  viewRow: (active) => ({
    display: 'flex', alignItems: 'center', gap: 1, height: 34, px: 1, mx: 1, mb: 0.5,
    borderRadius: `${t.radiusSm}px`, cursor: active ? 'default' : 'pointer',
    bgcolor: active ? t.blueSoft : 'transparent',
    '&:hover': { bgcolor: active ? t.blueSoft : '#F6F9FD' },
  }),
  defaultChip: {
    fontSize: 11, fontWeight: 500, lineHeight: '18px', px: 0.75,
    borderRadius: `${t.pill}px`, color: t.blue, bgcolor: t.blueSoft, border: `1px solid ${t.line}`,
  },
  editedPill: {
    fontSize: 11, fontWeight: 500, lineHeight: '18px', px: 0.75,
    borderRadius: `${t.pill}px`, color: t.muted, bgcolor: t.subtle, border: `1px solid ${t.line}`,
  },
  input: {
    '& .MuiInputBase-root': { fontSize: t.font, height: 34, borderRadius: `${t.radiusSm}px`, bgcolor: t.surface },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: t.lineStrong },
  },
  colRow: (locked) => ({
    display: 'flex', alignItems: 'center', gap: 0.5, height: 32, px: 0.5,
    borderRadius: `${t.radiusSm}px`, cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.6 : 1,
    bgcolor: 'transparent', zIndex: 'auto',
    '&:hover': { bgcolor: locked ? 'transparent' : '#F6F9FD' },
  }),
  checkbox: { p: 0.5, color: t.lineStrong, '&.Mui-checked': { color: t.blue } },
  link: { ...btnBase, height: 24, px: 0.75, fontWeight: 500, color: t.blue, '&:hover': { bgcolor: t.blueSoft } },
  footer: { borderTop: `1px solid ${t.line}`, bgcolor: t.subtle, px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 },
  primary: { ...btnBase, fontWeight: 600, bgcolor: t.blue, '&:hover': { bgcolor: t.blueHover, boxShadow: 'none' } },
  quiet: { ...btnBase, px: 1, color: t.muted, '&:hover': { bgcolor: 'transparent', color: t.ink } },
};

const columnList = (api) => (api?.getAllColumns?.() ?? []).filter((col) => !col.field.startsWith('__') && col.field !== 'Comp');

/** Columns popover. The saved-view row and Save / Discard only render when this grid has a layout (`columnLayout`). */
const ColumnViewMenu = ({ columnLayout = null }) => {
  const apiRef = useGridApiContext();
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState('');
  const [viewName, setViewName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [widthsDirty, setWidthsDirty] = useState(false);
  const [, bump] = useReducer((n) => n + 1, 0);
  const ignoreWidth = useRef(false);
  const searchRef = useRef(null);
  const edited = Boolean(columnLayout?.canSave) && (columnLayout.isDirty || widthsDirty);

  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent) return undefined;
    const unsubVisibility = api.subscribeEvent('columnVisibilityModelChange', bump);
    const unsubWidth = columnLayout?.canSave
      ? api.subscribeEvent('columnWidthChange', () => { if (!ignoreWidth.current) setWidthsDirty(true); })
      : undefined;
    return () => { unsubVisibility(); unsubWidth?.(); };
  }, [apiRef, columnLayout?.canSave]);

  const api = apiRef.current;
  const cols = columnList(api);
  const visible = new Set((api?.getVisibleColumns?.() ?? []).map((col) => col.field));
  const shown = cols.filter((col) => visible.has(col.field)).length;
  const hideable = cols.filter((col) => col.hideable !== false);
  const allShown = hideable.every((col) => visible.has(col.field));
  const q = query.trim().toLowerCase();
  const list = q ? cols.filter((col) => (col.headerName || col.field).toLowerCase().includes(q)) : cols;

  const setAll = (on) => api.setColumnVisibilityModel(Object.fromEntries(hideable.map((col) => [col.field, on])));

  const setDefault = async () => {
    setSaving(true);
    try {
      await columnLayout.setDefault();
      toast.success('Default view updated');
    } catch (error) {
      logger.error('Error setting default view:', error);
      toast.error('Failed to set default view');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const name = viewName.trim();
    if (!name || name.toLowerCase() === 'default') {
      setNameError(true);
      return;
    }
    setSaving(true);
    try {
      await columnLayout.saveView(apiRef, name);
      setWidthsDirty(false);
      setViewName('');
      setNameError(false);
      toast.success('View saved');
    } catch (error) {
      logger.error('Error saving view:', error);
      toast.error('Failed to save view');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<ViewWeekOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        sx={s.trigger}
      >
        <Box component="span" sx={s.ellipsis}>{columnLayout ? (columnLayout.viewName || 'Default') : 'Columns'}</Box>
        {edited && <Box component="span" sx={s.editedPill}>Edited</Box>}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQuery(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { sx: s.paper, role: 'dialog', 'aria-label': columnLayout ? 'Views and columns' : 'Columns' },
          transition: { onEntered: () => searchRef.current?.focus() },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'min(640px, 80vh)' }}>
          {columnLayout && (
            <>
              <Box sx={s.sectionHead}>
                <Typography sx={s.sectionTitle}>Views</Typography>
              </Box>
              <Box sx={{ pb: 0.5 }}>
                {(columnLayout.views || [{ name: 'Default' }]).map((view) => {
                  const active = view.name === (columnLayout.viewName || 'Default');
                  return (
                    <Box
                      key={view.name}
                      onClick={() => {
                        if (active) return;
                        ignoreWidth.current = true;
                        columnLayout.selectView(view.name);
                        setWidthsDirty(false);
                        requestAnimationFrame(() => { ignoreWidth.current = false; });
                      }}
                      sx={s.viewRow(active)}
                    >
                      <CheckIcon sx={{ fontSize: 16, color: t.blue, visibility: active ? 'visible' : 'hidden' }} />
                      <Typography noWrap sx={{ flex: 1, fontSize: t.font, fontWeight: active ? 600 : 400, color: active ? t.blue : t.ink }}>{view.name}</Typography>
                      {active && edited && <Box component="span" sx={s.editedPill}>Edited</Box>}
                      {view.name === 'Default' && <Box component="span" sx={s.defaultChip}>Default</Box>}
                      {columnLayout.canSave && view.name !== 'Default' && (
                        <IconButton
                          size="small"
                          aria-label={`Delete ${view.name}`}
                          onClick={(e) => { e.stopPropagation(); setPendingDelete(view.name); }}
                          sx={{ p: 0.25, color: t.muted, '&:hover': { color: t.danger } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Divider sx={{ borderColor: t.line }} />
            </>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={s.sectionHead}>
              <Typography sx={s.sectionTitle}>Columns</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={s.meta}>{shown} of {cols.length} shown</Typography>
                <Button size="small" onClick={() => setAll(!allShown)} sx={s.link}>{allShown ? 'Hide all' : 'Show all'}</Button>
              </Box>
            </Box>
            <Box sx={{ px: 1.5, pb: 1 }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                inputRef={searchRef}
                placeholder="Find a column"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: t.faint }} />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { 'aria-label': 'Find a column' },
                }}
                sx={s.input}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 96, maxHeight: 280, overflowY: 'auto', px: 1 }}>
              {list.map((col) => {
                const locked = col.hideable === false;
                return (
                  <Box component="label" key={col.field} sx={s.colRow(locked)}>
                    <Checkbox
                      size="small"
                      checked={visible.has(col.field)}
                      disabled={locked}
                      onChange={(e) => api.setColumnVisibility(col.field, e.target.checked)}
                      sx={s.checkbox}
                    />
                    <Typography noWrap sx={{ fontSize: t.font, color: t.ink }}>{col.headerName || col.field}</Typography>
                  </Box>
                );
              })}
              {!list.length && (
                <Typography sx={{ ...s.meta, px: 1, py: 1.5 }}>No columns match &ldquo;{query.trim()}&rdquo;.</Typography>
              )}
            </Box>
            <Typography sx={{ ...s.meta, px: 2, pt: 0.75, pb: 1.25 }}>
              Drag headers to reorder and edges to resize.{columnLayout?.canSave ? ' Saving keeps both.' : ''}
            </Typography>
          </Box>

          {columnLayout?.canSave && (
            <Box sx={s.footer}>
              <Button onClick={setDefault} disabled={saving || (columnLayout.viewName || 'Default') === 'Default'} sx={s.quiet}>Set Default</Button>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                <TextField
                  size="small"
                  placeholder="View name"
                  value={viewName}
                  error={nameError}
                  onChange={(e) => { setViewName(e.target.value); if (nameError) setNameError(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                  slotProps={{ htmlInput: { maxLength: 100, 'aria-label': 'New view name' } }}
                  sx={{ ...s.input, width: '75%' }}
                />
              </Box>
              <Button variant="contained" onClick={save} disabled={saving} sx={s.primary} endIcon={saving ? <CircularProgress size={14} /> : null}>
                Save
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
      <StableMuiDialog
        open={Boolean(pendingDelete)}
        onClose={() => !saving && setPendingDelete(null)}
        maxWidth={false}
        PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 420 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 500, color: dialogColors.textPrimary, m: '0 0 4px' }}>
            Delete view
          </Typography>
          <Typography sx={{ fontSize: 13, color: dialogColors.textSecondary, m: '0 0 20px' }}>
            Delete &ldquo;{pendingDelete}&rdquo;? This cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
            <Button onClick={() => setPendingDelete(null)} disabled={saving} sx={CANCEL_BUTTON_SX}>Cancel</Button>
            <Button
              variant="contained"
              disableElevation
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await columnLayout.deleteView(pendingDelete);
                  if (pendingDelete === columnLayout.viewName) setWidthsDirty(false);
                  setPendingDelete(null);
                  toast.success('View deleted');
                } catch (error) {
                  logger.error('Error deleting view:', error);
                  toast.error('Failed to delete view');
                } finally {
                  setSaving(false);
                }
              }}
              sx={PRIMARY_BUTTON_SX}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </StableMuiDialog>
    </>
  );
};

export default ColumnViewMenu;
